import { useRef, type ReactNode } from 'react';
import {
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue,
} from 'framer-motion';

/**
 * The motion vocabulary for Glanz Luxe — BUILD-PLAN §5, §40, §47.
 *
 * These are the techniques that separate an editorial site from a template:
 * masked line reveals, scroll-linked parallax, a genuine infinite marquee and
 * magnetic controls. Each is subtle by design — §5 rejects "cheap-looking
 * animations" and §47 caps this at "minimal but meaningful".
 *
 * Every component here returns its content unanimated when the visitor has
 * `prefers-reduced-motion` set. That is a correctness requirement, not a
 * courtesy: hiding content behind an animation that never runs would make the
 * page unreadable for those users (§40).
 */

/** The house easing curve — a slow settle, closer to physical motion than a linear fade. */
const EASE = [0.16, 1, 0.3, 1] as const;

/* ------------------------------------------------------------------------ */
/* Masked line reveal                                                        */
/* ------------------------------------------------------------------------ */

/**
 * Reveals a line of text as though rising from behind a mask.
 *
 * The mask is the parent's `overflow-hidden`; the child slides up from fully
 * below it. This reads as far more considered than a plain fade because the
 * text has a physical edge to emerge from — the standard technique in
 * fashion and editorial work.
 */
export function RevealLine({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' });

  /*
   * `block` is kept in the reduced-motion path too. Without it the spans
   * become inline and consecutive headline lines run together mid-sentence —
   * the animation fallback must not change the layout, only remove motion.
   */
  if (reduced) {
    return <span className={`block ${className}`}>{children}</span>;
  }

  return (
    <span ref={ref} className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: '110%' }}
        animate={inView ? { y: '0%' } : { y: '110%' }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/**
 * Staggers a group of children into view.
 *
 * `once` matters: content that re-animates every time it scrolls back into
 * view is distracting and makes a long page feel restless.
 */
export function RevealGroup({
  children,
  className = '',
  stagger = 0.08,
}: {
  children: ReactNode;
  className?: string;
  stagger?: number;
}) {
  const reduced = useReducedMotion();

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '0px 0px -80px 0px' }}
      variants={{ visible: { transition: { staggerChildren: stagger } } }}
    >
      {children}
    </motion.div>
  );
}

/** A child of RevealGroup. */
export function RevealItem({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduced = useReducedMotion();

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 26 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------------ */
/* Scroll-linked parallax                                                    */
/* ------------------------------------------------------------------------ */

/**
 * Moves an element against the scroll direction as it passes the viewport.
 *
 * Scroll-*linked* rather than scroll-triggered: the transform is driven
 * directly by scroll position, so it tracks the finger exactly instead of
 * playing a canned animation. A spring smooths the raw value, which removes
 * the jitter that makes naive parallax feel cheap.
 */
export function Parallax({
  children,
  /** Travel in pixels across the full pass. Negative moves upward. */
  distance = 60,
  className = '',
}: {
  children: ReactNode;
  distance?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const smooth: MotionValue<number> = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 24,
    restDelta: 0.001,
  });

  const y = useTransform(smooth, [0, 1], [distance, -distance]);

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------------ */
/* Marquee                                                                   */
/* ------------------------------------------------------------------------ */

/**
 * Continuously scrolling strip.
 *
 * The track holds two identical copies and translates by exactly -50%, so the
 * second copy lands precisely where the first began and the loop is seamless.
 * Duplicating content this way means the animation never has to measure the
 * DOM or recalculate on resize.
 *
 * The duplicate is aria-hidden so screen readers announce the content once.
 */
export function Marquee({
  items,
  speed = 40,
  className = '',
}: {
  items: string[];
  /** Seconds for one full pass — higher is slower. */
  speed?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();

  const Strip = ({ hidden }: { hidden: boolean }) => (
    <ul
      className="flex shrink-0 items-center gap-x-14 pr-14"
      {...(hidden ? { 'aria-hidden': true } : {})}
    >
      {items.map((item) => (
        <li
          key={item}
          className="flex items-center gap-14 whitespace-nowrap text-[0.625rem] uppercase tracking-[0.32em]"
        >
          {item}
          <span aria-hidden="true" className="h-1 w-1 rounded-full bg-accent" />
        </li>
      ))}
    </ul>
  );

  // Static, centred fallback — the words still communicate without motion.
  if (reduced) {
    return (
      <div className={`flex justify-center overflow-hidden ${className}`}>
        <Strip hidden={false} />
      </div>
    );
  }

  return (
    <div className={`overflow-hidden ${className}`}>
      <motion.div
        className="flex w-max"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: speed, ease: 'linear', repeat: Infinity }}
      >
        <Strip hidden={false} />
        <Strip hidden />
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Magnetic control                                                          */
/* ------------------------------------------------------------------------ */

/**
 * Draws an element gently toward the cursor.
 *
 * Pointer-driven only, and disabled on coarse pointers where there is no
 * hover state to respond to — on touch it would either do nothing or fire
 * on tap, which feels like a glitch.
 */
export function Magnetic({
  children,
  strength = 0.25,
  className = '',
}: {
  children: ReactNode;
  strength?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  const x = useSpring(0, { stiffness: 200, damping: 18 });
  const y = useSpring(0, { stiffness: 200, damping: 18 });

  if (reduced) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      style={{ x, y }}
      className={className}
      onPointerMove={(event) => {
        if (event.pointerType !== 'mouse') return;

        const bounds = ref.current?.getBoundingClientRect();
        if (!bounds) return;

        // Offset from the element's centre, scaled down so the pull is felt
        // rather than seen.
        x.set((event.clientX - (bounds.left + bounds.width / 2)) * strength);
        y.set((event.clientY - (bounds.top + bounds.height / 2)) * strength);
      }}
      onPointerLeave={() => {
        x.set(0);
        y.set(0);
      }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------------ */
/* Scroll progress                                                           */
/* ------------------------------------------------------------------------ */

/** A hairline gold progress bar pinned to the top of the viewport. */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });

  return (
    <motion.div
      aria-hidden="true"
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-50 h-px origin-left bg-accent"
    />
  );
}
